import type { QueryClient } from '@tanstack/react-query';
import { accountStore } from '@/services/auth/store';
import { accountUserKeys } from '@/services/db/user/entity';
import { getAccountUserQueryOptions } from '@/services/db/user/queries';
import { safeAvatarUrl } from './message';

/**
 * Avatars are resolved by user id on the client: the realtime connection info is set once, but
 * profile image URLs are signed and expire, so the account-user query (cached by react-query,
 * one request per user) is the source of truth.
 */

const urlOf = (user: { profile_image_url?: string | null } | undefined) => safeAvatarUrl(user?.profile_image_url);

/** Where bridge peers get their avatar: the resolved (query-cached) URL, fetched on first sight. */
export type AvatarSource = {
  /** The already-cached avatar URL for `userId`, without fetching. */
  lookup(userId: number): string | undefined;
  /** Fetches the avatar of every user not asked for yet, once each. */
  ensure(userIds: number[]): void;
};

/**
 * Reads avatars from the query cache and fetches the missing ones once each, calling `onResolved`
 * when a lookup finishes so subscribers hear about the new peer list. A server-sent avatar is only
 * the fallback while (or if) resolution has nothing.
 */
export function createAvatarSource(queryClient: QueryClient, onResolved: () => void): AvatarSource {
  // Users already asked for: a failed lookup is not retried on every peer-list change.
  const asked = new Set<number>();

  return {
    lookup(userId) {
      const accountId = accountStore.get()?.id;
      if (accountId === undefined) return undefined;
      return urlOf(queryClient.getQueryData(accountUserKeys.detail(accountId, String(userId))));
    },
    ensure(userIds) {
      const accountId = accountStore.get()?.id;
      if (accountId === undefined) return;
      for (const id of userIds) {
        if (asked.has(id)) continue;
        if (queryClient.getQueryData(accountUserKeys.detail(accountId, String(id))) !== undefined) continue;
        asked.add(id);
        queryClient
          .fetchQuery(getAccountUserQueryOptions(accountId, String(id)))
          .catch(() => undefined)
          .then(onResolved);
      }
    },
  };
}
